(() => {
  const shopRoot = window.Shopify?.routes?.root || '/';
  const getDrawer = () => document.getElementById('TaztyCartDrawer');
  let busy = false;
  let queued = false;
  let publishing = false;
  let opener;

  const status = message => {
    const node = getDrawer()?.querySelector('[data-cart-status]');
    if (node) node.textContent = message;
  };
  const maximumFor = input => {
    const maximum = Number(input?.max);
    return input?.max == null || input.max === '' || !Number.isFinite(maximum) ? Infinity : maximum;
  };
  const setBusy = value => {
    busy = value;
    const drawer = getDrawer();
    drawer?.setAttribute('aria-busy', String(value));
    drawer?.querySelectorAll('[data-cart-step], [data-cart-quantity], [data-cart-remove], [name="checkout"]')
      .forEach(node => { node.disabled = value; });
  };
  const updateHeader = count => {
    document.querySelectorAll('.site-header__cart').forEach(link => {
      const badge = link.querySelector('b');
      if (badge) badge.textContent = count;
      link.setAttribute('aria-label', `Cart, ${count} items`);
    });
  };
  const updateShipping = () => {
    const drawer = getDrawer();
    const panel = drawer?.querySelector('[data-cart-panel]');
    if (!panel) return;
    window.TaztyShipping?.update(drawer.querySelector('[data-shipping-bar]'),
      Number(panel.dataset.total), panel.dataset.currency || window.Shopify?.currency?.active || 'USD');
  };
  const open = (trigger, shouldRefresh = true) => {
    const drawer = getDrawer();
    if (!drawer?.showModal) return false;
    if (!drawer.open) {
      opener = trigger || document.activeElement;
      // A quick-view add can open the cart; don't leave two modal dialogs stacked.
      document.querySelector('[data-tazty-quick-view-dialog][open]')?.close();
      drawer.showModal();
    }
    updateShipping();
    if (shouldRefresh) refresh();
    return true;
  };
  const render = html => {
    if (typeof html !== 'string') throw new Error('Cart display could not be refreshed. Please open the full cart.');
    const incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-panel]');
    const drawer = getDrawer();
    const current = drawer?.querySelector('[data-cart-panel]');
    if (!incoming || !current) throw new Error('Cart display could not be refreshed. Please open the full cart.');
    const active = document.activeElement;
    const focusedInside = current.contains(active);
    const key = active?.closest?.('[data-cart-key]')?.dataset.cartKey;
    const action = active?.hasAttribute?.('data-cart-quantity') ? '[data-cart-quantity]'
      : active?.hasAttribute?.('data-cart-remove') ? '[data-cart-remove]'
      : active?.dataset?.cartStep === '1' ? '[data-cart-step="1"]' : '[data-cart-step="-1"]';
    const scrollTop = current.querySelector('.tz-cart-drawer__body')?.scrollTop || 0;
    current.replaceWith(incoming);
    const incomingBody = incoming.querySelector('.tz-cart-drawer__body');
    if (incomingBody) incomingBody.scrollTop = scrollTop;
    updateHeader(Number(incoming.dataset.count));
    updateShipping();
    if (drawer.open && focusedInside) {
      const line = [...incoming.querySelectorAll('[data-cart-key]')].find(item => item.dataset.cartKey === key);
      (line?.querySelector(action) || incoming.querySelector('[data-cart-close]'))?.focus();
    }
  };
  async function refresh() {
    const drawer = getDrawer();
    if (!drawer) return;
    if (busy) { queued = true; return; }
    setBusy(true);
    status('');
    try {
      let warning = '';
      let sections;
      try { sections = (await window.TaztyPackDiscounts?.sync(null, { sections: [drawer.dataset.sectionId] }))?.sections; }
      catch (_) { warning = 'The pack discount could not be confirmed. Reopen your cart to check it before checkout.'; }
      if (typeof sections?.[drawer.dataset.sectionId] !== 'string') {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', drawer.dataset.sectionId);
        const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000) });
        if (!response.ok) throw new Error('Cart could not be loaded. Please try again or open the full cart.');
        sections = await response.json();
      }
      if (getDrawer() === drawer) render(sections[drawer.dataset.sectionId]);
      if (warning) status(warning);
    } catch (error) {
      status(error.message || 'Cart could not be loaded.');
    } finally {
      setBusy(false);
      if (queued) { queued = false; refresh(); }
    }
  }
  async function change(key, quantity) {
    if (busy || !key || !Number.isSafeInteger(quantity) || quantity < 0) return;
    const drawer = getDrawer();
    if (!drawer) return;
    setBusy(true);
    status('');
    try {
      const response = await fetch(`${shopRoot}cart/change.js`, {
        method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(15000),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: [drawer.dataset.sectionId], sections_url: window.location.pathname })
      });
      let cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || 'This quantity is unavailable.');
      let warning = '';
      try { cart = await window.TaztyPackDiscounts?.sync(cart, { sections: [drawer.dataset.sectionId] }) || cart; }
      catch (_) { warning = 'Quantity updated, but the pack discount could not be confirmed. Reopen your cart to check the total before checkout.'; }
      if (getDrawer() === drawer) {
        updateHeader(cart.item_count);
        if (cart.sections?.[drawer.dataset.sectionId]) render(cart.sections[drawer.dataset.sectionId]);
        else queued = true; // Mutation succeeded even if Shopify couldn't render its section.
      }
      if (warning) status(warning);
      publishing = true;
      document.dispatchEvent(new CustomEvent('tazty:cart-updated', { detail: cart }));
      publishing = false;
    } catch (error) {
      status(error.name === 'TimeoutError'
        ? 'The update could not be confirmed. Reopen the cart to check its current quantity before trying again.'
        : (error.message || 'Cart could not be updated.'));
      // Restore a manually typed quantity when Shopify rejected the update.
      drawer.querySelectorAll('[data-cart-quantity]').forEach(input => { input.value = input.defaultValue; });
    } finally {
      publishing = false;
      setBusy(false);
      if (queued) { queued = false; refresh(); }
    }
  }
  async function splitPackIncrease(line, quantity) {
    const drawer = getDrawer();
    const key = line?.dataset.cartKey;
    const variantId = Number(line?.dataset.cartVariantId);
    const discountedQuantity = Number(line?.dataset.cartDiscountedQuantity);
    const input = line?.querySelector('[data-cart-quantity]');
    const currentQuantity = Number(input?.defaultValue);
    if (busy || !drawer || !key || !Number.isSafeInteger(variantId) || variantId <= 0
      || !Number.isSafeInteger(discountedQuantity) || discountedQuantity <= 0
      || !Number.isSafeInteger(quantity) || quantity <= discountedQuantity) return;
    setBusy(true);
    status('');
    try {
      // Keep the product-page pack on its own fixed line. Extra cart quantity is
      // deliberately added without pack properties, so it stays full price.
      if (currentQuantity !== discountedQuantity) {
        const changeResponse = await fetch(`${shopRoot}cart/change.js`, {
          method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(15000),
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: discountedQuantity })
        });
        const changedCart = await changeResponse.json();
        if (!changeResponse.ok) throw new Error(changedCart.description || changedCart.message || 'This quantity is unavailable.');
      }
      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(15000),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: quantity - discountedQuantity }],
          sections: [drawer.dataset.sectionId], sections_url: window.location.pathname
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'This quantity is unavailable.');
      if (getDrawer() === drawer) {
        if (result.sections?.[drawer.dataset.sectionId]) render(result.sections[drawer.dataset.sectionId]);
        else queued = true;
      }
      publishing = true;
      document.dispatchEvent(new CustomEvent('tazty:cart-updated', { detail: result }));
      publishing = false;
    } catch (error) {
      status(error.name === 'TimeoutError'
        ? 'The update could not be confirmed. Reopen the cart before trying again.'
        : (error.message || 'Cart could not be updated.'));
      drawer.querySelectorAll('[data-cart-quantity]').forEach(quantityInput => { quantityInput.value = quantityInput.defaultValue; });
    } finally {
      publishing = false;
      setBusy(false);
      if (queued) { queued = false; refresh(); }
    }
  }
  const boot = () => {
    const drawer = getDrawer();
    if (!drawer || drawer.dataset.bound) return;
    drawer.dataset.bound = 'true';
    drawer.addEventListener('click', event => {
      if (event.target.closest('[data-cart-close]')) { drawer.close(); return; }
      if (event.target === drawer) {
        const bounds = drawer.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) drawer.close();
      }
    });
    drawer.addEventListener('close', () => {
      if (opener?.isConnected) opener.focus();
    });
    drawer.addEventListener('submit', event => { if (busy) event.preventDefault(); });
    updateShipping();
  };
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-cart-drawer-open]');
    if (trigger && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
      if (open(trigger)) event.preventDefault();
      return;
    }
    const control = event.target.closest('[data-cart-step], [data-cart-remove]');
    if (!control || !getDrawer()?.contains(control)) return;
    const line = control.closest('[data-cart-key]');
    const input = line.querySelector('[data-cart-quantity]');
    const increment = Math.max(1, Number(input.step) || 1);
    const quantity = control.hasAttribute('data-cart-remove') ? 0 : Math.max(0, Number(input.value) + Number(control.dataset.cartStep) * increment);
    const maximum = maximumFor(input);
    if (!control.hasAttribute('data-cart-remove') && quantity > maximum) {
      status(`The maximum available quantity is ${maximum}.`);
      return;
    }
    const discountedQuantity = Number(line.dataset.cartDiscountedQuantity || 0);
    if (!window.TaztyPackDiscounts?.enabled && !control.hasAttribute('data-cart-remove') && discountedQuantity > 0 && quantity > discountedQuantity) splitPackIncrease(line, quantity);
    else change(line.dataset.cartKey, quantity);
  });
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-cart-quantity]') || !getDrawer()?.contains(event.target)) return;
    const quantity = Number(event.target.value);
    if (!Number.isSafeInteger(quantity) || quantity < 0 || event.target.value === '') {
      event.target.value = event.target.defaultValue;
      status('Enter a whole quantity of zero or more.');
      return;
    }
    const maximum = maximumFor(event.target);
    if (quantity > maximum) {
      event.target.value = event.target.defaultValue;
      status(`The maximum available quantity is ${maximum}.`);
      return;
    }
    const line = event.target.closest('[data-cart-key]');
    const discountedQuantity = Number(line.dataset.cartDiscountedQuantity || 0);
    if (!window.TaztyPackDiscounts?.enabled && discountedQuantity > 0 && quantity > discountedQuantity) splitPackIncrease(line, quantity);
    else change(line.dataset.cartKey, quantity);
  });
  document.addEventListener('tazty:cart-updated', event => {
    if (publishing) return;
    if (event.detail?.item_count != null) updateHeader(event.detail.item_count);
    const drawer = getDrawer();
    if (event.detail?.openDrawer !== false && drawer?.dataset.openOnAdd === 'true') open(null, false);
    const html = event.detail?.sections?.[drawer?.dataset.sectionId];
    if (!busy && typeof html === 'string') {
      try { render(html); return; } catch (_) { /* Fetch a fresh section below. */ }
    }
    if (drawer?.open) refresh();
  });
  ['currency:change', 'shopify:currency:change', 'localization:change'].forEach(name =>
    document.addEventListener(name, () => { if (getDrawer()?.open) refresh(); }));
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:select', event => {
    if (event.target.contains(getDrawer())) open(null, false);
  });
  window.addEventListener('pageshow', () => { boot(); if (getDrawer()?.open) refresh(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
